<template>
  <MeetingCardShell :collapsible="collapsible" :default-expanded="defaultExpanded">
    <template #header-left>
      <!-- Time slot -->
      <span class="txt-body-m">{{ timeSlot }}</span>
      <!-- Name with external link icon -->
      <button class="flex items-center gap-1 text-inherit" @click.stop="handleViewProfile">
        <span class="txt-headline-m">{{ userName }}</span>
        <flag v-tooltip="userLanguageTooltip" :iso="userFlagIso" class="h-4! w-4! rounded-full border border-main-solid" />
        <ArrowUpRightIcon class="lucide-icon-m" />
      </button>
    </template>

    <template #header-right>
      <!-- Confirmed mode: Badge + 3-dots dropdown -->
      <template v-if="mode === 'confirmed'">
        <ChipBadge color="success" type="outlined" size="l" rounded>
          <template #prepend><CircleCheck class="lucide-icon-s" /></template>
          {{ t('Meetings.myMeetings.confirmed') }}
        </ChipBadge>
        <!-- 3-dots dropdown menu -->
        <DropDownSelect direction="right">
          <template #activator="{ open }">
            <button class="rounded-sm p-1 hover:bg-surface-elevation-4" @click.stop="open">
              <EllipsisVerticalIcon class="lucide-icon-m text-subtle" />
            </button>
          </template>
          <template #default="{ close }">
            <div class="min-w-[220px] py-1">
              <button
                class="flex w-full items-center gap-2 px-4 py-2 text-left text-body-m text-error hover:bg-surface-elevation-2"
                @click="handleCancelClick(close)"
              >
                <XIcon class="lucide-icon-s" />
                {{ t('Meetings.myMeetings.cancelMeeting') }}
              </button>
            </div>
          </template>
        </DropDownSelect>
      </template>

      <!-- Declined mode: Badge -->
      <template v-else-if="mode === 'declined'">
        <ChipBadge v-if="appointment.state === 'CANCELLED'" color="default" type="outlined" size="l" rounded>
          <template #prepend><CircleXIcon class="lucide-icon-s" /></template>
          {{ t('Meetings.declined.cancelledStatus') }}
        </ChipBadge>
        <ChipBadge v-else-if="appointment.state === 'DECLINED_AUTO'" color="default" type="outlined" size="l" rounded>
          <template #prepend><CircleXIcon class="lucide-icon-s" /></template>
          {{ t('Meetings.declined.autoDeclinedStatus') }}
        </ChipBadge>
        <ChipBadge v-else color="default" type="outlined" size="l" rounded>
          <template #prepend><CircleXIcon class="lucide-icon-s" /></template>
          {{ t('Meetings.declined.status') }}
        </ChipBadge>
      </template>

      <!-- Request mode: Accept + Decline buttons -->
      <template v-else>
        <button
          class="btn-primary-success-xs flex items-center gap-1.5"
          :disabled="actionLoading"
          @click.stop="emit('accept', appointment.id)"
        >
          {{ t('Meetings.requests.accept') }}
          <Check class="lucide-icon-m" />
        </button>
        <button class="btn-secondary-neutral-xs" :disabled="actionLoading" @click.stop="emit('decline', appointment.id)">
          {{ t('Meetings.requests.decline') }}
        </button>
      </template>
    </template>

    <template #body>
      <!-- User info section: Avatar + Position + Company -->
      <div class="flex items-center gap-3 border-b border-subtle bg-surface-elevation-1 p-4">
        <img :src="appointment.user.profileImage" :alt="userName" class="size-12 shrink-0 rounded-sm object-cover" />
        <div class="flex flex-col">
          <span v-if="appointment.user.position" class="txt-body-m">{{ appointment.user.position }}</span>
          <span v-if="appointment.user.companyName" class="txt-body-m">{{ appointment.user.companyName }}</span>
        </div>
      </div>

      <!-- Guest notes/message (if present) -->
      <div v-if="appointment.guestNotes" class="border-b border-subtle bg-surface-elevation-1 p-4">
        <p class="txt-body-m">{{ appointment.guestNotes }}</p>
      </div>

      <!-- Cancellation info (only in declined mode for cancelled appointments) -->
      <div v-if="mode === 'declined' && appointment.state === 'CANCELLED'" class="border-b border-subtle bg-surface-elevation-1 p-4">
        <div v-if="appointment.cancelledBy" class="mb-2">
          <span class="txt-body-m">{{ t('Meetings.declined.cancelledBy') }}: </span>
          <span class="txt-body-m">{{ appointment.cancelledBy.firstName }} {{ appointment.cancelledBy.lastName }}</span>
        </div>
        <div v-if="appointment.cancellationReason">
          <span class="txt-body-m">{{ t('Meetings.declined.cancellationReason') }}: </span>
          <span class="txt-body-m">{{ appointment.cancellationReason }}</span>
        </div>
      </div>

      <!-- Denial Reason (only in declined mode for denied appointments) -->
      <div
        v-if="mode === 'declined' && appointment.state !== 'CANCELLED' && appointment.denialReason"
        class="border-b border-subtle bg-surface-elevation-1 p-4"
      >
        <span class="txt-body-m">{{ t('Meetings.declined.reason') }}: </span>
        <span class="txt-body-m">{{ appointment.denialReason }}</span>
      </div>

      <!-- Internal Notes Section (all modes) -->
      <div class="bg-surface-elevation-1 p-4">
        <template v-if="!isEditingNotes">
          <div v-if="appointment.exhibitorNotes" class="flex items-start justify-between gap-2">
            <div>
              <span class="txt-body-m">{{ t('Meetings.myMeetings.internalNote') }}: </span>
              <span class="txt-body-m">{{ appointment.exhibitorNotes }}</span>
            </div>
            <button class="shrink-0 text-subtlest hover:text-subtle" @click="startEditingNotes">
              <PencilIcon class="lucide-icon-s" />
            </button>
          </div>
          <button v-else class="flex items-center gap-1 text-body-m-bold" @click="startEditingNotes">
            {{ t('Meetings.myMeetings.addInternalNote') }}
            <PlusIcon class="lucide-icon-s" />
          </button>
        </template>

        <template v-else>
          <textarea
            v-model="editedNotes"
            class="w-full rounded-sm border border-main p-2 text-body-s focus:border-primary focus:outline-none"
            rows="2"
            :placeholder="t('Meetings.myMeetings.notesPlaceholder')"
          />
          <div class="mt-2 flex gap-2">
            <button class="btn-primary-color-xs" :disabled="savingNotes" @click="saveNotes">
              {{ savingNotes ? t('common.saving') : t('common.save') }}
            </button>
            <button class="btn-secondary-neutral-xs" :disabled="savingNotes" @click="cancelEditingNotes">
              {{ t('common.cancel') }}
            </button>
          </div>
        </template>
      </div>
    </template>
  </MeetingCardShell>

  <VisitorProfileModal v-if="visitorProfileRecord" :show="showProfileModal" :visitor="visitorProfileRecord" @close="closeProfileModal" />
</template>

<script setup lang="ts">
import { ChipBadge } from '@ramp106/omrjs-core-ui'
import {
  ArrowUpRightIcon,
  CircleXIcon,
  EllipsisVerticalIcon,
  PencilIcon,
  PlusIcon,
  XIcon,
  CircleCheck,
  Check,
} from '@ramp106/omrjs-core-ui/icons'
import { MeetingCardShell, formatTimeRange } from '@ramp106/omrjs-events-ui'
import { useApolloClient } from '@vue/apollo-composable'
import { computed, inject, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import type { CompanyVisitorProfile } from '@/components/VisitorProfiles/types'
import VisitorProfileModal from '@/components/VisitorProfiles/VisitorProfileModal.vue'
import type { MeetingsAppointmentsQuery } from '@/gql/myomr'
import { MeetingsVisitorProfileDocument } from '@/gql/myomr'
import { languageCode } from '@/helpers/languageCode'
import DropDownSelect from '@/ui/DropDownSelect.vue'

type AppointmentNode = NonNullable<NonNullable<MeetingsAppointmentsQuery['meetings']>['appointments']>['nodes'][number]

const props = withDefaults(
  defineProps<{
    appointment: AppointmentNode
    mode?: 'confirmed' | 'request' | 'declined'
    actionLoading?: boolean
    collapsible?: boolean
    defaultExpanded?: boolean
  }>(),
  {
    mode: 'confirmed',
    actionLoading: false,
    collapsible: false,
    defaultExpanded: true,
  },
)

const emit = defineEmits<{
  (e: 'saveNotes', appointmentId: string, notes: string): void
  (e: 'accept', appointmentId: string): void
  (e: 'decline', appointmentId: string): void
  (e: 'cancel', appointmentId: string): void
}>()

const { t } = useI18n()

function handleCancelClick(close: () => void) {
  close()
  emit('cancel', props.appointment.id)
}

const userName = computed(() => {
  const { firstName, lastName, email } = props.appointment.user
  const parts = [firstName, lastName].filter(Boolean)
  return parts.length > 0 ? parts.join(' ') : email
})

const userFlagIso = computed(() => {
  return languageCode(props.appointment.user.locale)
})

const userLanguageTooltip = computed(() => {
  return props.appointment.user.locale === 'de' ? t('Meetings.myMeetings.prefersGerman') : t('Meetings.myMeetings.prefersEnglish')
})

const timeSlot = computed(() => formatTimeRange(props.appointment.startsAt, props.appointment.endsAt))

// Visitor profile modal
const showToast = inject<(message: string, type: 'success' | 'error') => void>('showToast', () => {})
const showProfileModal = ref(false)
const visitorProfileRecord = ref<CompanyVisitorProfile | null>(null)
const { resolveClient } = useApolloClient()

async function handleViewProfile() {
  try {
    const client = resolveClient()
    const { data } = await client.query({
      query: MeetingsVisitorProfileDocument,
      variables: { userId: props.appointment.user.id },
      fetchPolicy: 'network-only',
    })
    const vp = data?.meetings?.visitorProfile
    if (vp) {
      visitorProfileRecord.value = vp as CompanyVisitorProfile
      showProfileModal.value = true
    } else {
      showToast(t('Meetings.myMeetings.profileNotFound'), 'error')
    }
  } catch {
    showToast(t('common.errorOccurred'), 'error')
  }
}

function closeProfileModal() {
  showProfileModal.value = false
}

// Notes editing state
const isEditingNotes = ref(false)
const editedNotes = ref('')
const savingNotes = ref(false)

function startEditingNotes() {
  editedNotes.value = props.appointment.exhibitorNotes || ''
  isEditingNotes.value = true
}

function cancelEditingNotes() {
  isEditingNotes.value = false
  editedNotes.value = ''
}

async function saveNotes() {
  savingNotes.value = true
  emit('saveNotes', props.appointment.id, editedNotes.value)
}

// Expose method to reset editing state after save
function resetEditingState(success: boolean) {
  savingNotes.value = false
  if (success) {
    isEditingNotes.value = false
    editedNotes.value = ''
  }
}

defineExpose({ resetEditingState })
</script>
